/**
 * Settings ▸ Users (admin-only). Two jobs in one table: work the signup-approval
 * queue, and manage existing accounts — including revoking access from someone
 * who already has it.
 *
 * The five states come from the server as `user.status`; see
 * jcseo/api/admin_users.py. Which buttons a row gets is a function of that
 * status alone, so the UI never re-derives it from is_active/rejected_at.
 * "Unverified" is a signup that never clicked its email link: not yet in the
 * queue, but approvable — an admin vouching for the address counts.
 *
 * DELETE is the exception, and it is not a state. It sits outside the
 * status→actions table because it is available from every status, it is
 * irreversible, and it is the only action here that needs to show you what it
 * will cost before you take it — hence a dialog with a preview and a typed
 * confirmation rather than a window.confirm. The rules live in ./userDeletion.
 */
import { Fragment, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import UserAccessPanel from '../components/admin/UserAccessPanel';
import { useAuth } from '../contexts/AuthContext';
import { canOfferDelete, canSubmitDelete } from './userDeletion';
import type {
  AdminUser,
  AdminUserDeletionPreview,
  AdminUserStatus,
  AdminUserStatusFilter,
} from '../types';

const FILTERS: { key: AdminUserStatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'unverified', label: 'Unverified' },
  { key: 'approved', label: 'Approved' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_BADGE: Record<AdminUserStatus, { label: string; className: string }> = {
  approved: { label: 'Approved', className: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
  pending: { label: 'Pending', className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' },
  unverified: { label: 'Unverified', className: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300' },
  disabled: { label: 'Disabled', className: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  rejected: { label: 'Rejected', className: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' },
};

type UserAction = 'approve' | 'reject' | 'disable' | 'enable';

// Buttons offered per status. `danger` styles the destructive one; `confirm` is
// the prompt shown first (omitted for the additive actions).
const ACTIONS: Record<UserAction, { label: string; danger?: boolean; confirm?: (name: string) => string }> = {
  approve: { label: 'Approve' },
  reject: { label: 'Reject', danger: true, confirm: (n) => `Reject ${n}'s application?` },
  disable: {
    label: 'Disable',
    danger: true,
    // Every request re-checks is_active (auth.get_current_active_user), so this
    // takes effect on their next call — not just at next login.
    confirm: (n) => `Disable ${n}? They'll lose access immediately, including any active session, until re-enabled. Their account and work are kept.`,
  },
  enable: { label: 'Enable' },
};

const ACTIONS_FOR: Record<AdminUserStatus, UserAction[]> = {
  pending: ['approve', 'reject'],
  // Approving here also marks the email verified (the server does it), so an
  // admin can wave through someone they know without waiting on the link.
  unverified: ['approve', 'reject'],
  approved: ['disable'],
  disabled: ['enable'],
  rejected: ['approve'],
};

function fullName(u: AdminUser): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.username;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function UserApprovals() {
  const { user: currentUser } = useAuth();
  const [filter, setFilter] = useState<AdminUserStatusFilter>('all');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Per-user in-flight flag so we can disable that row's buttons.
  const [acting, setActing] = useState<Record<number, boolean>>({});
  // Which user's access/credits panel is open under their row (one at a time).
  const [openAccess, setOpenAccess] = useState<number | null>(null);
  // The delete dialog. `target` opening is what triggers the preview fetch;
  // everything else here is scoped to that one dialog and cleared when it shuts.
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [preview, setPreview] = useState<AdminUserDeletionPreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.listUsers(filter);
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAction = async (user: AdminUser, action: UserAction) => {
    const confirmMsg = ACTIONS[action].confirm;
    if (confirmMsg && !window.confirm(confirmMsg(fullName(user)))) return;

    setActing((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      if (action === 'approve') await api.approveUser(user.id);
      else if (action === 'reject') await api.rejectUser(user.id);
      else if (action === 'disable') await api.disableUser(user.id);
      else await api.enableUser(user.id);
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} user`);
    } finally {
      setActing((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  // Opening the dialog and loading the preview are one step: a dialog that
  // offered Delete before it knew what would be destroyed would be the exact
  // blind delete this screen is trying to avoid.
  const openDelete = async (user: AdminUser) => {
    setDeleteTarget(user);
    setPreview(null);
    setPreviewError('');
    setConfirmText('');
    try {
      setPreview(await api.previewUserDeletion(user.id));
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to load deletion preview');
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setPreview(null);
    setPreviewError('');
    setConfirmText('');
  };

  const handleDelete = async () => {
    if (!deleteTarget || !canSubmitDelete({ preview, typed: confirmText, deleting })) return;
    setDeleting(true);
    setPreviewError('');
    try {
      await api.deleteUser(deleteTarget.id, confirmText.trim());
      setDeleteTarget(null);
      setPreview(null);
      setConfirmText('');
      await fetchUsers();
    } catch (err) {
      // Stays in the dialog: the message says why (built pages, most likely),
      // and closing would lose it.
      setPreviewError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Users</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Review accounts that have signed up and manage existing ones. Approved
        users can sign in; rejected and disabled users are kept on record but
        blocked from logging in. Delete removes an account for good - use it on
        signups you're never going to approve.
      </p>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              filter === f.key
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto">
        {loading ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">Loading…</div>
        ) : users.length === 0 ? (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            No {filter === 'all' ? '' : filter} users.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Signed up</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((u) => {
                const badge = STATUS_BADGE[u.status];
                const busy = !!acting[u.id];
                // The server refuses to let an admin lock themselves out; don't
                // offer the buttons that would try.
                const isSelf = u.id === currentUser?.id;
                const actions = ACTIONS_FOR[u.status].filter(
                  (a) => !(isSelf && (a === 'reject' || a === 'disable'))
                );
                return (
                  <Fragment key={u.id}>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {fullName(u)}
                      {u.is_admin && (
                        <span className="ml-2 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium">
                          Admin
                        </span>
                      )}
                      {isSelf && (
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">you</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                      {/* Products, plans and credits — only meaningful once
                          they can sign in. */}
                      {u.status === 'approved' && (
                        <button
                          type="button"
                          onClick={() => setOpenAccess(openAccess === u.id ? null : u.id)}
                          className="px-3 py-1 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-blue-400"
                        >
                          {openAccess === u.id ? 'Close' : 'Access'}
                        </button>
                      )}
                      {actions.map((a) => (
                        <button
                          key={a}
                          onClick={() => handleAction(u, a)}
                          disabled={busy}
                          title={
                            a === 'approve' && u.status === 'unverified'
                              ? 'Approving also marks the email as verified'
                              : undefined
                          }
                          className={`px-3 py-1 rounded-md text-sm font-medium disabled:opacity-50 ${
                            ACTIONS[a].danger
                              ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40'
                              : 'text-white bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {ACTIONS[a].label}
                        </button>
                      ))}
                      {/* Last, and quieter than the status actions: it's always
                          available but almost never the right button. */}
                      {canOfferDelete(u, currentUser?.id) && (
                        <button
                          onClick={() => openDelete(u)}
                          disabled={busy}
                          title="Permanently delete this account and everything it owns"
                          className="px-3 py-1 rounded-md text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                  {openAccess === u.id && (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 bg-gray-50 dark:bg-gray-900/40">
                        <UserAccessPanel userId={u.id} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={deleteTarget !== null}
        onClose={closeDelete}
        title={deleteTarget ? `Delete ${fullName(deleteTarget)}?` : 'Delete account'}
      >
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              This permanently removes <span className="font-medium">{deleteTarget.email}</span>{' '}
              and everything the account owns. It cannot be undone. To take away
              access while keeping their work, close this and use Disable.
            </p>

            {previewError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
                {previewError}
              </div>
            )}

            {!preview && !previewError && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Checking what this would delete…
              </div>
            )}

            {preview && !preview.deletable && (
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-3 py-2 rounded text-sm">
                This account has{' '}
                {preview.blocked_by.map((b) => `${b.count} ${b.label.toLowerCase()}`).join(', ')}.
                Pages are never deleted as a side effect of deleting something
                else - delete them first, then delete the account.
              </div>
            )}

            {preview && preview.deletable && (
              <div className="text-sm">
                {preview.destroys.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">
                    This account has no projects, sites or other work. Nothing
                    else is lost.
                  </p>
                ) : (
                  <>
                    <p className="text-gray-600 dark:text-gray-300 mb-2">Also deleted:</p>
                    <ul className="border border-gray-200 dark:border-gray-700 rounded divide-y divide-gray-200 dark:divide-gray-700">
                      {preview.destroys.map((item) => (
                        <li
                          key={item.label}
                          className="flex justify-between px-3 py-1.5 text-gray-700 dark:text-gray-200"
                        >
                          <span>{item.label}</span>
                          <span className="font-medium tabular-nums">{item.count}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {preview && preview.deletable && (
              <div>
                <label
                  htmlFor="delete-confirm"
                  className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                >
                  Type <span className="font-mono">{preview.email}</span> to confirm
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  autoComplete="off"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={closeDelete}
                disabled={deleting}
                className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!canSubmitDelete({ preview, typed: confirmText, deleting })}
                className="px-3 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
